import { memo, useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useHistory } from 'react-router-dom';
import { useStore } from 'store/StoreContext';
import { callGetGroupApi, GroupWithLoginUser, GroupType } from 'requests/RequestGroup';
import { GetLoginDisplayItemResData, callGetLoginDisplayItemApi } from 'requests/RequestLogin';
import { RoutePath } from 'consts/RoutePath';
import { CircularProgress, Backdrop } from '@material-ui/core';
import classes from './override.module.scss';
import {
	convertObjectToGetParameter,
	convertObjectToGetParameterWithEncode,
} from 'common/ObjectToGetParameterConverter';
import { HttpStatus } from 'consts/HttpStatus';
import { unescapeHtml } from 'common/HtmlEscaper';
import { Hr } from 'components/atoms/Hr';
import { ResponseData } from 'hooks/UseNext4To5';
import { findGroupAuthByKey, isAdmin, isMember, GroupAuth } from 'consts/GroupAuthConst';
import { BirthDay } from 'components/molecules/BirthDay';
import { callGetSiteApi, GetSiteResData } from 'requests/RequestSite';
import { ReactError } from 'consts/ReactError';
import { hasScope } from 'utils/oidcUtils';
import {
	callCommonLoginV1Api,
	callCommonLoginV2Api,
	CommonLoginV1ResData,
	CommonLoginV2ResData,
} from 'requests/RequestLogin';
import { useRedirectLoginRequest } from 'hooks/UseRedirectLoginRequest';

type Props = {};

export type registerState = {
	isMemberRegistered?: boolean;
};

export const MemberAddTemplate = memo((_props: Props) => {
	const history = useHistory<ResponseData>();
	const dto = history.location.state;
	const location = useHistory<{ isMemberRegistered: boolean }>().location;
	const isMemberRegistered1 =
		typeof location.state === 'undefined' || typeof location.state.isMemberRegistered === 'undefined'
			? false
			: location.state.isMemberRegistered;
	const search = location.search;
	const queryParams = new URLSearchParams(search);
	const [store , dispatch] = useStore(); // eslint-disable-line
	const { redirect_uri } =
		typeof queryParams.get('redirect_uri') === 'undefined' ? undefined : useRedirectLoginRequest(dispatch);
	const [displayItem, setDisplayItem] = useState<GetLoginDisplayItemResData>(undefined);
	// 家族情報を取得する
	const [groupData, setGroupData] = useState<GroupWithLoginUser[]>([]);
	const [getGroupLoading, setGetGroupLoading] = useState(false);
	const [site, setSite] = useState<GetSiteResData>(undefined);
	const [displayItemLoading, setDisplayItemLoading] = useState(false);
	const [actionError, setActionError] = useState('');
	const [loginError, setLoginError] = useState('');
	const isActionError = actionError !== '';
	useEffect(() => {
		const execute = async () => {
			setGetGroupLoading(true);
			const getGroupResult = await callGetGroupApi(GroupType.FAMILY);
			if (!!store.loginUserData.gidpk && getGroupResult.status === HttpStatus.OK && !!getGroupResult.data) {
				if (getGroupResult.data.group_list.length === 0) {
					setGroupData([]);
					setGetGroupLoading(false);
					return;
				}
				// ログインユーザのグループごとの情報をグループのヘッダに持つ
				const groupWithLoginUsers = getGroupResult.data.group_list.map((g) => {
					return { ...g, loginUser: g.gid_list.find((u) => u.gidpk === store.loginUserData.gidpk) };
				});
				setGroupData(groupWithLoginUsers);
			}
			setGetGroupLoading(false);
		};
		execute();
	}, []);
	useEffect(() => {
		if (store.registerUserData.isV2ParamsOk) {
			const execute = async () => {
				setDisplayItemLoading(true);
				const results = await Promise.all([
					callGetLoginDisplayItemApi({
						gid_dp: store.authorizeV2Data.gid_dp,
						site_cd: store.authorizeV2Data.client_id,
					}),
					callGetSiteApi(store.authorizeV2Data.client_id),
				]);

				const isError = results.some((r) => r.status !== HttpStatus.OK || !r.data);
				if (isError) {
					setActionError(ReactError.E0008);
					history.push(RoutePath.ERROR);
					setDisplayItemLoading(false);
					return;
				}

				const [getDisplayItemResult, getSiteResult] = results;

				setDisplayItem(getDisplayItemResult.data);
				setSite(getSiteResult.data);
				setDisplayItemLoading(false);
			};
			execute();
		}
	}, [store.authorizeV2Data.gid_dp, store.registerUserData.isV2ParamsOk]);
	const [returnTransitioning, setReturnTransitioning] = useState(false);
	const returnTop = async () => {
		setReturnTransitioning(true);
		if (!!store.redirectLoginData.redirect_uri) {
			window.location.href = decodeURIComponent(store.redirectLoginData.redirect_uri);
		} else if (!!redirect_uri) {
			window.location.href = decodeURIComponent(redirect_uri);
		} else if (store.registerUserData.isV2ParamsOk) {
			setDisplayItemLoading(true);
			const result = await callCommonLoginV2Api({
				gid: store.registerUserData.gid,
				pw: store.registerUserData.pass,
				...store.authorizeV2Data,
			});
			if (result.status !== HttpStatus.OK || !result.data) {
				setActionError(ReactError.E9999);
			}
			const data = result.data as CommonLoginV2ResData;
			const getParameters = convertObjectToGetParameter(data);
			// リダイレクト先urlがエンコードされたままなのでデコードしてからリダイレクトする
			window.location.href = `${
				store.authorizeV2Data.redirect_uri
					? decodeURIComponent(store.authorizeV2Data.redirect_uri)
					: displayItem.redirect_url
			}?${getParameters}`;
			setDisplayItemLoading(false);
		} else {
			history.push(RoutePath.MY_PAGE);
		}
	};
	// OIDCの場合の属性提供同意画面遷移
	const transAttrAgree = () => {
		const getParameters = convertObjectToGetParameterWithEncode(
			store.authorizeV2Data,
			'code_challenge',
			'redirect_uri',
			'nonce',
		);
		history.push(`${RoutePath.ATTRIBUTE_AGREEMENT}?${getParameters}`);
	};
	const scrollRef = useRef(null);
	useEffect(() => {
		scrollRef.current.scrollIntoView();
		document.title = 'GakkenIDマイページ｜家族情報';
	}, []);

	return (
		<>
			<Backdrop className={classes.backdrop} open={getGroupLoading}>
				<CircularProgress color="inherit" />
			</Backdrop>
			<section className="section mypage" ref={scrollRef}>
				<h1 className="page-head">GakkenこどもIDを登録する</h1>

				{groupData.length > 0 ? (
					<>
						<div className="section-in">
							{isMemberRegistered1 === true ? (
								<>
									<div className="thankyou-box">
										<img src="images/img-registered.svg" alt="登録が完了しました！" />
									</div>
								</>
							) : null}
							<div className="section-head">
								<span className={classes.sectionHeadH1}>「家族」 一覧</span>
							</div>
							<div className="info-box">
								<div className={classes.memberRow}>
									<div className={`${classes.memberRowHeadColumn} ${classes.iconColumn}`} />
									<div className={`${classes.memberRowHeadColumn} ${classes.nameColumn}`}>GakkenID／お名前</div>
									<div className={`${classes.memberRowHeadColumn} ${classes.birthColumn}`}>生年月日</div>
									<div className={`${classes.memberRowHeadColumn} ${classes.linkColumn}`} />
								</div>
								<Hr />
								{groupData[0].gid_list.map((u) => {
									const auth = findGroupAuthByKey(u);
									return (
										<>
											<div className={classes.memberRow}>
												<div className={`${classes.gidRowDataColumn} ${classes.gidColumn}`}>{`${u.gid}`}</div>
											</div>
											<div className={classes.memberRow}>
												<div
													className={`${classes.memberRowDataColumn} ${classes.iconColumn} ${classes.iconDataColumn}`}
												>
													<img className={classes.memberIcon} src={`images/${auth.image}`} alt="" />
													<div className={classes.memberLabel}>{auth.name}</div>
												</div>
												<div
													className={`${classes.memberRowDataColumn} ${classes.nameColumn} ${classes.nameDataColumn}`}
												>{`${u.lastname}　${u.firstname}`}</div>
												<div
													className={`${classes.memberRowDataColumn} ${classes.birthColumn} ${classes.birthDataColumn}`}
												>
													<BirthDay birth={u.birth} />
												</div>
											</div>
											<Hr />
										</>
									);
								})}
							</div>
							{!isMember(findGroupAuthByKey(groupData[0].loginUser)) && (
								<div className={'btn-box'}>
									{(isAdmin(findGroupAuthByKey(groupData[0].loginUser)) ||
										groupData[0].loginUser.member_updatable_flg === 1) && (
										<div className={'btn gr ' + classes.memberBtnBoxAddBtn}>
											<Link
												to={{
													pathname: RoutePath.MEMBER_INPUT,
													state: { groupId: groupData[0].group_id, fromPage: 'memberAdd' },
												}}
											>
												<span>こどもIDを追加する</span>
											</Link>
										</div>
									)}
									<div className="btn gr">
										{displayItemLoading ? (
											<CircularProgress />
										) : (
											<>
												{hasScope(store.authorizeV2Data.scope) && Boolean(site?.need_claim_agreement_flg) ? (
													<button onClick={transAttrAgree}>
														{displayItem?.member_regist_complete ? displayItem?.member_regist_complete : `次へ`}
													</button>
												) : (
													<button onClick={returnTop}>サービスへ戻る</button>
												)}
											</>
										)}
									</div>
								</div>
							)}
							{isMember(findGroupAuthByKey(groupData[0].loginUser)) && (
								<>
									<div className={'btn-box'}>
										<div className="btn disabled">
											<button disabled>こどもIDを追加する</button>
										</div>
										<div className="btn gr">
											<button onClick={returnTop}>サービスへ戻る</button>
										</div>
									</div>
									<div className="new-member-txt">こどもIDでは追加できません。</div>
								</>
							)}
							{(isAdmin(findGroupAuthByKey(groupData[0].loginUser)) ||
								groupData[0].loginUser.member_updatable_flg === 1) && (
								<div className="new-member-txt">
									<a href="/my-page" target="_blank">
										家族情報を変更する場合はこちら
									</a>
								</div>
							)}
						</div>
					</>
				) : (
					<>
						<div className="section-in">
							<div className="section-head">
								<span className={classes.sectionHeadH1}>「家族」 一覧</span>
							</div>
							<div className="section-head">
								<span className={classes.sectionHeadH2}>所属しているユーザーはまだいません。</span>
							</div>
							<div className="info-box">
								<div>
									「追加」ボタンから
									{`${GroupAuth.MEMBER.name}（メールアドレスを持っていないユーザー）の登録ができます。`}
								</div>
							</div>
							<div className={'btn-box'}>
								<div className={'btn gr'}>
									<Link
										to={{
											pathname: RoutePath.MEMBER_INPUT,
											state: { fromPage: 'memberAdd' },
										}}
									>
										<span>追加</span>
									</Link>
								</div>
							</div>
						</div>
					</>
				)}
			</section>
		</>
	);
});
